import React from 'react';
import Header from './components/layout/Header/Header';
import Sidebar from './components/layout/Sidebar/Sidebar';
import ClientHeader from './components/layout/Header/ClientHeader';
import ClientSidebar from './components/layout/Sidebar/ClientSidebar';
import ClientPreparingPlan from './pages/client/PreparingPlan';
import useIsMobile from './components/hooks/useIsMobile';

import { ToastProvider } from './components/ui/toast/ToastContext';
import { ConfirmProvider } from './components/ui/confirm/ConfirmContext';

const hasObjectData = (value) => value && Object.keys(value).length > 0;

const App = ({ children, sidebarItems = [], currentPath, currentAdmin, currentClient, plannerList }) => {
  const isAdmin = hasObjectData(currentAdmin);
  const isClient = hasObjectData(currentClient);
  const isAuthenticated = isAdmin || isClient;

  const isMobile = useIsMobile(1024); // < 1024 -> mobile/tablet
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(true);
  const [adminSidebarItems, setAdminSidebarItems] = React.useState(sidebarItems);

  React.useEffect(() => {
    setAdminSidebarItems(sidebarItems);
  }, [sidebarItems]);

  React.useEffect(() => {
    const updateSidebarItems = (event) => setAdminSidebarItems(event.detail.items);

    window.addEventListener('admin-sidebar-items-updated', updateSidebarItems);
    return () => window.removeEventListener('admin-sidebar-items-updated', updateSidebarItems);
  }, []);

  const openMenu = () => setMobileMenuOpen(true);
  const closeMenu = () => setMobileMenuOpen(false);

  const toggleSidebar = () => setIsSidebarCollapsed((prev) => !prev);

  const adminLayoutRole = currentAdmin?.accessProfile?.area || currentAdmin?.role || 'admin';

  // If not authenticated, render only the children (e.g., login page)
  if (!isAuthenticated) {
    return (
      <ToastProvider>
        <ConfirmProvider>
          <>{children}</>
        </ConfirmProvider>
      </ToastProvider>
    );
  }

  const adminViewport = (
    <ToastProvider>
      <ConfirmProvider>
        <div className="flex flex-col h-screen font-sans bg-site">
          <Header currentAdmin={currentAdmin} role={adminLayoutRole} plannerList={plannerList} />

          <div className="flex flex-1 overflow-hidden h-full">
            <Sidebar
              items={adminSidebarItems}
              currentPath={currentPath}
              collapsed={isSidebarCollapsed}
              onToggle={toggleSidebar}
              role={adminLayoutRole}
            />

            <main className="flex-1 pt-5 px-5 pb-5 overflow-auto main-background">
              {children || <div className="text-gray-400">Carregando...</div>}
            </main>
          </div>
        </div>
      </ConfirmProvider>
    </ToastProvider>
  );

  const clientViewport = (
    <ToastProvider>
      <ConfirmProvider>
        <div className="h-screen flex bg-site">
          {!isMobile ? (
            <ClientSidebar
              items={sidebarItems}
              currentPath={currentPath}
              currentClient={currentClient}
              isMobile={false}
            />
          ) : (
            <ClientSidebar
              items={sidebarItems}
              currentPath={currentPath}
              currentClient={currentClient}
              isMobile
              open={mobileMenuOpen}
              onClose={closeMenu}
            />
          )}

          <div className="flex-1 flex flex-col min-w-0">
            <ClientHeader
              currentClient={currentClient}
              isMobile={isMobile}
              onMenuClick={openMenu}
              item={sidebarItems.find((item) => item.href === currentPath)}
            />

            <main className="flex-1 overflow-auto px-4 sm:px-6 py-4">
              {currentClient?.preparingPlan ? (
                <ClientPreparingPlan />
              ) : (
                children || <div className="text-gray-400">Carregando...</div>
              )}
            </main>
          </div>
        </div>
      </ConfirmProvider>
    </ToastProvider>
  );

  return isClient ? clientViewport : isAdmin ? adminViewport : null;
};

export default App;
